// The onboarding personalization vocabulary: the step ids the wizard declares,
// the flow variant they belong to, and the clinic-size answer the flow collects.
// `PersonalizationFormData` stays app-side — it holds app types
// (`ClinicianTypeInput`, `BookingIntent`) this inert door cannot carry.

import { OrganisationSize } from '@heidi/schema/ml-scribe';

export type ClinicSize = 'only-me' | '2-10' | '10-50' | '50-100' | 'over-100';

const CLINIC_SIZE_TO_ORGANISATION_SIZE: Record<ClinicSize, OrganisationSize> = {
  'only-me': OrganisationSize.JUST_ME,
  '2-10': OrganisationSize.TWO_TO_FIVE,
  '10-50': OrganisationSize.SIX_TO_TWENTY,
  '50-100': OrganisationSize.TWENTY_ONE_TO_FIFTY,
  'over-100': OrganisationSize.FIFTY_ONE_PLUS,
};

/** The backend enum for a clinic-size option; what every onboarding surface submits. */
export function mapClinicSizeToOrganisationSize(
  clinicSize: ClinicSize | null
): OrganisationSize | undefined {
  return clinicSize ? CLINIC_SIZE_TO_ORGANISATION_SIZE[clinicSize] : undefined;
}

const ORGANISATION_SIZE_TO_CLINIC_SIZE = new Map<OrganisationSize, ClinicSize>(
  (Object.keys(CLINIC_SIZE_TO_ORGANISATION_SIZE) as ClinicSize[]).map((clinicSize) => [
    CLINIC_SIZE_TO_ORGANISATION_SIZE[clinicSize],
    clinicSize,
  ])
);

/** The clinic-size option for a stored backend size; the inverse of `mapClinicSizeToOrganisationSize`. */
export function mapOrganisationSizeToClinicSize(
  organisationSize: OrganisationSize | null | undefined
): ClinicSize | null {
  return organisationSize
    ? (ORGANISATION_SIZE_TO_CLINIC_SIZE.get(organisationSize) ?? null)
    : null;
}

export type PersonalizationStep =
  | 'about-you'
  | 'name'
  | 'role'
  | 'clinic'
  | 'invite-team'
  /**
   * Guided-tour arm only: the same invite screen, declared at an earlier
   * position in the step list. The wizard keys position by step id, so a step
   * cannot occupy two slots — hence a second id rather than a moved one.
   */
  | 'tour-invite-team'
  | 'mic-permissions'
  | 'mic-test'
  | 'note-ready'
  | 'note-edit'
  /** Guided-tour arm only: `note-edit`'s three accordion tasks, one per screen. */
  | 'tour-detail'
  | 'tour-artefacts'
  | 'tour-evidence'
  | 'template-creation'
  | 'template-loading'
  | 'template-review'
  | 'document-selection'
  | 'completion';

/**
 * Gated by `release-new-onboarding-flow`: `experimental-2` and `experimental-3`
 * both → `learn-basics` (the tour is a layout of that flow). No variant selects
 * `v2` any more; it is retired with its step components in a follow-up.
 */
export type OnboardingFlowKind = 'v2' | 'learn-basics';
